import React, { useEffect, useState } from 'react';
import { usePluginInstallation } from './PluginInstallationContext';
import { trpc } from '@/lib/api/trpc';
import { useToast } from '@/components/ui/use-toast';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { useNavigate, useParams } from 'react-router-dom';
import { ConfigEditorModal } from '@/components/config-editor/ConfigEditorModal';
import { PluginInstallationWizard } from '../plugin-installation-wizard';

interface PluginInstallationOrchestratorProps {
  storeId: string;
  /** Optional funnel ID from context (step editor) */
  contextFunnelId?: string;
  /** Callback to open config editor at parent level (instanceId, funnelId) */
  onOpenConfigEditor?: (instanceId: string, funnelId?: string) => void;
  /** Callback when editor closes (used by step editor to close marketplace dialog) */
  onEditorClose?: () => void;
  /** Read-only mode for public marketplace */
  readOnly?: boolean;
}

/**
 * Orchestrates the plugin installation flow:
 * 1. Takes plugin + context from PluginInstallationContext
 * 2. Creates preview instance
 * 3. Opens config editor
 * 4. Handles cleanup and navigation
 */
export function PluginInstallationOrchestrator({
  storeId,
  contextFunnelId,
  onOpenConfigEditor,
  onEditorClose,
  readOnly = false,
}: PluginInstallationOrchestratorProps) {
  const { toast } = useToast();
  const utils = trpc.useUtils();
  const params = useParams();
  const simpleNavigate = useNavigate();

  // Only use store navigation if we have a store context (not public marketplace)
  const isPublicMarketplace = storeId === 'public-marketplace' || readOnly || !params.id;
  const storeNav = !isPublicMarketplace ? useStoreNavigate() : null;

  const navigate = storeNav?.navigate || simpleNavigate;
  const storeRoutes = storeNav?.storeRoutes;

  const installation = usePluginInstallation();

  const {
    plugin,
    selectedDeploymentId,
    selectedPreset,
    context,
    configEditorOpen,
    configEditorInstanceId,
    wizardOpen,
    sessionId,
  } = installation;

  // Track target funnel for navigation after editor closes
  const [targetFunnelId, setTargetFunnelId] = useState<string | null>(null);

  // Track processed session to prevent duplicate installs
  const [processedSessionId, setProcessedSessionId] = useState<string | null>(null);

  // Get deployment
  const deployment =
    plugin?.deployments.find((d) => d.deploymentId === selectedDeploymentId) || plugin?.deployments[0];

  // Reset processedSessionId when sessionId changes (new installation started)
  useEffect(() => {
    if (sessionId) {
      console.log('[Orchestrator] New session detected, resetting processedSessionId', { sessionId });
      setProcessedSessionId(null);
    }
  }, [sessionId]);

  // If onOpenConfigEditor is provided AND it's a function, delegate config editor to parent
  useEffect(() => {
    console.log('[Orchestrator] Delegation check:', {
      configEditorOpen,
      configEditorInstanceId,
      onOpenConfigEditor: typeof onOpenConfigEditor,
      hasCallback: !!onOpenConfigEditor,
    });

    if (configEditorOpen && configEditorInstanceId && typeof onOpenConfigEditor === 'function') {
      console.log('[Orchestrator] Delegating config editor to parent:', configEditorInstanceId);
      onOpenConfigEditor(configEditorInstanceId, targetFunnelId || undefined);
      // Close local config editor since parent will handle it
      installation.closeConfigEditor();
    }
  }, [configEditorOpen, configEditorInstanceId, onOpenConfigEditor, installation, targetFunnelId]);

  // Mutation to install builder plugin (copy + instantiate)
  const installBuilderMutation = trpc.plugins.installBuilderPlugin.useMutation({
    onSuccess: (result) => {
      console.log('[Orchestrator] ✅ Builder plugin installed:', result);

      utils.plugins.listInstances.invalidate();
      utils.plugins.list.invalidate();
      utils.plugins.getAvailableInstancesForStepType.invalidate();

      toast({
        title: 'Builder Plugin Installed',
        description: result.message,
      });

      // Store funnel ID for navigation after editor closes
      if (context?.funnelId) {
        setTargetFunnelId(context.funnelId);
      }

      // Open config editor with the new instance
      console.log('[Orchestrator] 🎬 Opening config editor for instance:', result.instanceId);
      installation.openConfigEditor(result.instanceId, context?.funnelId);
    },
    onError: (error) => {
      toast({
        title: 'Installation Failed',
        description: error.message,
        variant: 'destructive',
      });
      installation.closeWizard();
      setProcessedSessionId(null); // Allow retry
    },
  });

  // Mutation to create preview instance (for non-builder plugins)
  const instantiateMutation = trpc.plugins.instantiate.useMutation({
    onSuccess: (result) => {
      console.log('[Orchestrator] ✅ Instantiate mutation success:', {
        instanceId: result.instanceId,
        resultKeys: Object.keys(result),
        fullResult: result,
      });

      utils.plugins.listInstances.invalidate();
      utils.plugins.list.invalidate();
      utils.plugins.getAvailableInstancesForStepType.invalidate();

      toast({
        title: 'Instance Created',
        description: 'Opening configuration editor...',
      });

      // Store funnel ID for navigation after editor closes
      if (context?.funnelId) {
        setTargetFunnelId(context.funnelId);
      }

      // Open config editor with the new instance
      console.log('[Orchestrator] 🎬 Opening config editor for instance:', result.instanceId);
      installation.openConfigEditor(result.instanceId, context?.funnelId);
    },
    onError: (error) => {
      toast({
        title: 'Installation Failed',
        description: error.message,
        variant: 'destructive',
      });
      installation.closeWizard();
      setProcessedSessionId(null); // Allow retry
    },
  });

  // Auto-install when context is complete (from step editor)
  useEffect(() => {
    console.log('[Orchestrator] Auto-install useEffect triggered:', {
      sessionId,
      processedSessionId,
      hasPlugin: !!plugin,
      hasDeployment: !!deployment,
      hasContext: !!context,
      isPending: instantiateMutation.isPending,
      isSuccess: instantiateMutation.isSuccess,
      configEditorOpen,
      isPublicMarketplace,
    });

    // Skip in public marketplace (read-only mode)
    if (isPublicMarketplace) {
      console.log('[Orchestrator] Skipping auto-install: public marketplace mode');
      return;
    }

    // Only proceed if we have all required data
    if (!plugin || !deployment || !context) {
      console.log('[Orchestrator] Skipping auto-install: missing requirements');
      return;
    }

    // Skip if already processing this deployment
    if (instantiateMutation.isPending) {
      console.log('[Orchestrator] Skipping auto-install: mutation in progress');
      return;
    }

    // Skip if we already processed this session
    if (sessionId === processedSessionId) {
      console.log('[Orchestrator] Skipping auto-install: session already processed');
      return;
    }

    // Also skip if config editor is open (unless it's a new session, which is handled above)
    // But for extra safety:
    if (configEditorOpen && sessionId === processedSessionId) {
      console.log('[Orchestrator] Skipping auto-install: config editor open');
      return;
    }

    console.log('[Orchestrator] Auto-installing with context:', {
      sessionId,
      plugin: plugin.name,
      deployment: deployment.version,
      context,
    });

    // Mark this session as processed
    setProcessedSessionId(sessionId);

    // Only send installation-specific config (stepType)
    // Backend will load and merge preset config from deployment.configPresets[presetName]
    const installationConfig = {
      stepType: context.stepType,
    };

    console.log('[Orchestrator] 📦 Installation config being sent:', {
      presetName: selectedPreset,
      installationConfig,
      deploymentId: deployment.deploymentId,
      hasConfigPresets: !!deployment.configPresets,
      availablePresets: deployment.configPresets ? Object.keys(deployment.configPresets) : [],
    });

    // Check if this is a builder plugin
    const isBuilder = deployment.isBuilder === true;

    if (isBuilder) {
      // Builder plugin: Use special install endpoint that copies deployment
      console.log('[Orchestrator] 🎨 Installing builder plugin (will copy deployment)');
      installBuilderMutation.mutate({
        sourceDeploymentId: deployment.deploymentId,
        storeId,
        presetName: selectedPreset || 'default',
        config: installationConfig,
      });
    } else {
      // Regular plugin: Create preview instance
      console.log('[Orchestrator] 📦 Installing regular plugin');
      instantiateMutation.mutate({
      deploymentId: deployment.deploymentId,
      storeId,
      config: installationConfig, // Only installation config (stepType)
      presetName: selectedPreset || 'default', // Selected preset name - backend will load full preset
        isPreview: true,
        reuseIfExists: false, // Always create new instance when coming from funnel editor
      });
    }
  }, [plugin, deployment, context, storeId, configEditorOpen, sessionId, processedSessionId, selectedPreset, installBuilderMutation, instantiateMutation]);

  // Handle wizard success
  const handleWizardSuccess = (result: {
    instanceId: string;
    funnelId?: string;
    shouldOpenEditor: boolean;
  }) => {
    // Store funnel ID for navigation after editor closes
    if (result.funnelId) {
      setTargetFunnelId(result.funnelId);
    }

    // Mark session as processed since wizard handled it
    setProcessedSessionId(sessionId);

    // The wizard already created the instance
    // Open the config editor if requested
    if (result.shouldOpenEditor) {
      installation.openConfigEditor(result.instanceId, result.funnelId);
    } else {
      // If not opening editor, just reset
      installation.reset();
      toast({
        title: 'Plugin Installed',
        description: 'You can now configure and publish it.',
      });
    }
  };

  // Handle config editor close
  const handleEditorClose = () => {
    installation.closeConfigEditor();

    // If we have context (from step editor) and we're already in that funnel, just close marketplace
    if (contextFunnelId && targetFunnelId === contextFunnelId) {
      // We're already in the right funnel editor, just close the marketplace dialog
      setTargetFunnelId(null);
      setProcessedSessionId(null); // Reset for next installation
      if (onEditorClose) {
        onEditorClose();
      }
    }
    // Navigate to funnel editor if a funnel was created/selected and we're not already there
    else if (targetFunnelId && storeRoutes) {
      // Show success toast with instructions
      toast({
        title: 'Configuration Saved',
        description: 'Go to the funnel editor to publish and mount this plugin to a step',
      });

      // Navigate to funnel editor with correct org prefix
      navigate(storeRoutes.STOREFRONT_EDITOR(targetFunnelId));
      setTargetFunnelId(null);
      setProcessedSessionId(null); // Reset for next installation
    }

    // Refresh after editor closes
    utils.plugins.list.invalidate();
    utils.plugins.listInstances.invalidate();

    // Reset installation state
    installation.reset();
  };

  // Debug: Log current state
  console.log('[Orchestrator] Render state:', {
    hasPlugin: !!plugin,
    hasContext: !!context,
    wizardOpen,
    configEditorOpen,
    configEditorInstanceId,
  });

  // Render wizard if plugin selected but no context (user needs to choose funnel/step)
  if (plugin && !context && wizardOpen) {
    console.log('[Orchestrator] Rendering wizard');
    return (
      <PluginInstallationWizard
        open={wizardOpen}
        onOpenChange={(open) => {
          if (!open) {
            installation.closeWizard();
            setProcessedSessionId(null); // Reset when wizard closes
          }
        }}
        plugin={plugin}
        selectedDeploymentId={selectedDeploymentId || undefined}
        selectedPreset={selectedPreset || 'default'}
        storeId={storeId}
        onSuccess={handleWizardSuccess}
      />
    );
  }

  // Render config editor if open (only if not delegated to parent)
  if (configEditorOpen && configEditorInstanceId) {
    if (typeof onOpenConfigEditor === 'function') {
      console.log('[Orchestrator] ⚠️ Config editor open but delegated to parent - not rendering locally');
      return null;
    }

    console.log('[Orchestrator] ✅ Rendering config editor locally (no parent delegation)');
    return (
      <ConfigEditorModal instanceId={configEditorInstanceId} isPreview={true} onClose={handleEditorClose} />
    );
  }

  console.log(
    '[Orchestrator] Rendering nothing (waiting for auto-install or wizard, or delegated to parent)',
  );
  return null;
}
